import { ExpTable, ExpTableProps } from "@mlpa/core-ui-core-open_ocean_ui/Organisms/Tables/ExpTable";
import { EntityMappings } from "graphqlBase/__utils__/entityMappings";
import debounce from "lodash/debounce";
import * as React from "react";
import { useRecoilState } from "recoil";
import { makeGridPaginationState, makeGridState, TableStateExtended, useRegisterGridApiRef } from "../gridStatePersist";
import { BaseRow, DataGridFactoryParams } from "../types";

export type ExpTableWrapperProps<R extends BaseRow> = Omit<ExpTableProps, "columns" | "rows"> & {
  columns: ExpTableProps["columns"];
  rows: R[];
  totalCount?: number;
};

export function ExpTableFactory<R extends BaseRow, E extends keyof EntityMappings>({
  apiRef,
  executeQuery,
  tableMode = "server",
  persistance,
  tableId,
  persistDelay = 1000,
}: DataGridFactoryParams) {
  const ExpTableWrapper: React.FC<ExpTableWrapperProps<R>> = ({
    rows,
    columns,
    totalCount,
    paginationModel: controlledPaginationModel,
    onPaginationModelChange: controlledOnPaginationModelChange,
    onSortModelChange: controlledOnSortModelChange,
    onFilterModelChange: controlledOnFilterModelChange,
    ...props
  }) => {
    const serverSideLogic = tableMode === "server";
    const tableState = makeGridState(tableId, persistance);
    const [persistedTableState, setTableState] = useRecoilState(tableState);
    useRegisterGridApiRef(tableId, apiRef);
    const tablePaginationState = makeGridPaginationState({
      tableId,
      persistType: "session",
      initState: controlledPaginationModel,
    });
    const [paginationModel, setPaginationModel] = useRecoilState(tablePaginationState);

    const rowCountRef = React.useRef(totalCount ?? 0);
    const rowCount = React.useMemo(() => {
      // During server-side refetches (e.g. pagination), Apollo temporarily
      // reports totalCount as 0 before the new page arrives. If we pass 0
      // to the grid while on page > 0, MUI resets the page to 0.
      // Preserve the previous rowCount until real data arrives.
      if (totalCount !== undefined && (totalCount > 0 || paginationModel.page === 0)) {
        rowCountRef.current = totalCount;
      }
      return rowCountRef.current;
    }, [totalCount, paginationModel.page]);

    const persistState = React.useMemo(
      () =>
        debounce((updates: Partial<TableStateExtended>) => {
          if (persistance) {
            setTableState((prevState) => ({
              ...prevState,
              ...updates,
            }));
          }
        }, persistDelay),
      [persistance, setTableState, persistDelay]
    );

    return (
      <ExpTable
        {...props}
        apiRef={apiRef as React.MutableRefObject<any>}
        columns={columns}
        rows={rows}
        paginationMode={serverSideLogic ? "server" : "client"}
        sortingMode={serverSideLogic ? "server" : "client"}
        filterMode={serverSideLogic ? "server" : "client"}
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={(model, details) => {
          controlledOnPaginationModelChange?.(model, details);
          setPaginationModel(model);
          setTimeout(() => {
            if (serverSideLogic) executeQuery();
          }, 1);
        }}
        onSortModelChange={(model, details) => {
          controlledOnSortModelChange?.(model, details);
          persistState({ sorting: { sortModel: model } });
          setTimeout(() => {
            if (serverSideLogic) executeQuery();
          }, 1);
        }}
        onFilterModelChange={(model, details) => {
          controlledOnFilterModelChange?.(model, details);
          persistState({ filter: { filterModel: model } });
          setTimeout(() => {
            if (serverSideLogic) executeQuery();
          }, 1);
        }}
      />
    );
  };

  return ExpTableWrapper;
}
